import React, { useState } from "react";
import { WellStatus, WellPosition } from "../../Content";
import "./WellPlate.scss";

import ReactMarkdown from "react-markdown";
import rehypeRaw from "rehype-raw";
import { SimManager } from "@learningblocks/simulation-engine";

interface WellPlateProps {
  handleWellClick: (position: WellPosition) => void;
  getWellStatus: (position: WellPosition) => WellStatus;
  isDispense?: boolean;
  wellsContent?: { [key: WellPosition]: object };
  wellsColors?: Record<
    string,
    Record<string, string | number | { value: number }>
  >;
}

const WellPlate = ({
  handleWellClick,
  getWellStatus,
  isDispense,
  wellsContent,
  wellsColors,
}: WellPlateProps) => {
  const columns = Array.from({ length: 12 }, (_, i) => i + 1);
  const rows = ["A", "B", "C", "D", "E", "F", "G", "H"];

  const [tooltip, setTooltip] = useState<{
    row: string;
    col: number;
    content: string;
    top: number;
    left: number;
  } | null>(null);

  const getWellContent = (position: WellPosition): string =>
    wellsContent?.[position]
      ? Object.keys(wellsContent[position])
          .map((key) => {
            const obj = SimManager.getInstance().getObject(key);
            return obj?.getLabel().text ?? key; // fallback to key if no label
          })
          .join(", ")
      : "Empty";

  const getWellColor = (position: WellPosition): string | undefined => {
    if (!wellsColors) return undefined;
    const row = position.charAt(0);
    const col = position.slice(1);
    return wellsColors[row]?.color as string;
  };

  const [selectedWell, setSelectedWell] = useState<{
    row: string;
    col: number;
  } | null>(null);

  const handleWellPlateClick = (
    row: string,
    col: number,
    event:
      | React.MouseEvent<HTMLDivElement>
      | React.KeyboardEvent<HTMLDivElement>
  ) => {
    const position = `${row}${col}` as WellPosition;

    handleWellClick(position);
    if (!isDispense) {
      const content = getWellContent(position);
      const target = event.currentTarget as HTMLDivElement;
      const rect = target.getBoundingClientRect();
      const containerRect = target.parentElement?.getBoundingClientRect();
      if (containerRect) {
        setTooltip({
          row,
          col,
          content,
          top: rect.top - containerRect.top + 39,
          left: rect.left - containerRect.left + 45,
        });
      }
      setSelectedWell({ row, col });
    }
  };
  return (
    <div className="microplate-container" aria-label="Microplate Wells">
      <div className="svg-card">
        <svg
          className="top-left-svg"
          xmlns="http://www.w3.org/2000/svg"
          width="8"
          height="259"
          viewBox="0 0 8 259"
          fill="none"
        >
          <path d="M0 16L8 0V251L0 259V16Z" fill="#E2F2F9" />
        </svg>
        <svg
          className="bottom-left-svg"
          xmlns="http://www.w3.org/2000/svg"
          width="36"
          height="33"
          viewBox="0 0 36 33"
          fill="none"
        >
          <path d="M0 8L8 0L36 25L28 33L0 8Z" fill="#CEE9F2" />
        </svg>
      </div>
      <div className="svg-card">
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="28"
          height="300"
          viewBox="0 0 28 300"
          fill="none"
        >
          <path d="M0 24.3243L28 0V300L0 275.676V24.3243Z" fill="#F2FBFE" />
        </svg>
      </div>
      <div className="microplate">
        <div className="grid-container">
          <div className="empty-corner" />
          {columns.map((col) => (
            <div key={`header-${col}`} className="column-label">
              {col}
            </div>
          ))}
          {rows.map((row) => (
            <React.Fragment key={`row-${row}`}>
              <div className="row-label">{row}</div>
              {columns.map((col) => {
                const position = `${row}${col}` as WellPosition;
                let wellStatusClass = getWellStatus(position);
                const isSelected =
                  selectedWell?.row === row && selectedWell?.col === col;
                if (isSelected && !wellStatusClass.includes("selected")) {
                  wellStatusClass += " selected";
                }
                const wellColor = getWellColor(position);
                const wellStyle =
                  wellColor && getWellStatus(position) === "filled"
                    ? { backgroundColor: wellColor }
                    : {};
                return (
                  <div
                    key={`well-${row}-${col}`}
                    data-title={`${row}${col}`}
                    className={`well ${wellStatusClass}`}
                    style={wellStyle}
                    onClick={(event) => handleWellPlateClick(row, col, event)}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(event) => {
                      if (event.key === "Enter" || event.key === " ") {
                        handleWellPlateClick(row, col, event);
                      }
                    }}
                  ></div>
                );
              })}
            </React.Fragment>
          ))}
        </div>
      </div>
      {tooltip && (
        <div
          className="tooltip"
          style={{ top: tooltip.top - 40, left: tooltip.left + 10 }}
        >
          <p>
            {tooltip.row}
            {tooltip.col}
          </p>
          <p>
            <ReactMarkdown
              rehypePlugins={[rehypeRaw]}
              components={{ p: React.Fragment }}
            >
              {tooltip.content}
            </ReactMarkdown>
          </p>
        </div>
      )}
      <div className="svg-card-end">
        <svg
          xmlns="http://www.w3.org/2000/svg"
          height="8"
          viewBox="0 0 436 8.5"
          fill="none"
        >
          <path d="M8 0H436L420 8H0L8 0Z" fill="#ACCFDC" />
        </svg>
      </div>
    </div>
  );
};

export default WellPlate;
